import { ArrowLeftRight, KeyRound, MonitorSmartphone } from 'lucide-react'
import { useId, useState } from 'react'

import { QueryState } from '@krp/sale-ui'
import { Button } from '@krp/ui-kit'

import '../../../../shared/ui/aqsi.css'
import type { MineVm, MoveWayVm, RmkActions } from '../../model/types'
import '../rmk.css'

/**
 * Рабочее место закреплено за другой вкладкой (RP-CA5): здесь ничего не отправляется, пока кассир не введёт свой код. После кода
 * работа переходит в эту вкладку, а прежняя сама покажет такой же экран. Решает сервер; экран только просит код и пересказывает отказ.
 * Работа под своим именем кода не имеет — переносится кнопкой.
 *
 * Чей код спрашивать, решает модель (`way`, К-120): кассир работы связан с кассиром нашей системы — его личный код, как в «Начале работы»;
 * иначе — код кассира кабинета AQSI. Прежний способ остаётся и для связанного кассира — кнопкой под полем. Пока список кассиров нашей системы
 * не прочитан (или чтение не удалось), код не отправляется.
 */
export function OtherTabScreen({ mine, way, actions }: { mine: MineVm; way: MoveWayVm; actions: RmkActions }) {
  const id = useId()
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [cabinetCode, setCabinetCode] = useState(false)
  const needsCode = way.kind !== 'none'
  const waiting = way.kind === 'reading' || way.kind === 'failed'
  const own = way.kind === 'own' ? way.own : null
  const byOwn = own !== null && !cabinetCode
  const move = async () => {
    if (busy || waiting || (needsCode && value.trim() === '')) return
    setBusy(true)
    setProblem(null)
    const credential = value
    setValue('')
    try {
      setProblem(await actions.moveHere(credential, cabinetCode))
    } finally {
      setBusy(false)
    }
  }
  const switchWay = () => {
    setCabinetCode((current) => !current)
    setValue('')
    setProblem(null)
  }
  return (
    <section className="aqsi-screen rmk-gate" aria-labelledby={`${id}-title`}>
      <header className="aqsi-toolbar">
        <h2 id={`${id}-title`}>
          <MonitorSmartphone size={18} aria-hidden="true" /> Рабочее место открыто в другой вкладке
        </h2>
      </header>
      <p className="aqsi-caption">
        Касса {mine.registerName}
        {mine.cashierName !== null ? `, кассир ${mine.cashierName}` : ''}. Пока рабочее место там, из этой вкладки ничего не отправляется.
        {needsCode ? ' Введите код кассира, чтобы перенести рабочее место сюда: в той вкладке оно закроется.' : ' Перенесите рабочее место сюда: в той вкладке оно закроется.'}
      </p>
      {(way.kind === 'reading' || way.kind === 'failed') && (
        <QueryState requestIdHidden query={way.query} what="Кассиры нашей системы" emptyText="Кассиров нет" onRetry={actions.reload} />
      )}
      <form
        className="aqsi-form rmk-code"
        aria-label="Перенос рабочего места в эту вкладку"
        onSubmit={(event) => {
          event.preventDefault()
          void move()
        }}
      >
        {needsCode && (
          <>
            <label className="krp-field__label" htmlFor={id}>
              {byOwn ? `Личный код: ${own.displayName}` : `Код кассира ${mine.cashierName ?? ''}`}
            </label>
            <input
              id={id}
              className="rmk-code__input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              data-lpignore="true"
              data-1p-ignore="true"
              value={value}
              disabled={busy || waiting}
              onChange={(event) => setValue(event.target.value)}
            />
          </>
        )}
        <Button
          type="submit"
          variant="primary"
          icon={<KeyRound size={16} aria-hidden="true" />}
          actionId="aqsi.rmk.move-here"
          busy={busy}
          disabled={busy || waiting || (needsCode && value.trim() === '')}
          disabledReason={waiting ? 'Сначала должен прочитаться список кассиров нашей системы.' : 'Введите код кассира.'}
        >
          Перенести сюда
        </Button>
      </form>
      {own !== null && (
        <Button variant="ghost" icon={<ArrowLeftRight size={14} aria-hidden="true" />} actionId="aqsi.rmk.move-code-way" disabled={busy} onClick={switchWay}>
          {cabinetCode ? `Ввести личный код: ${own.displayName}` : 'Ввести код кассира кабинета AQSI'}
        </Button>
      )}
      {problem !== null && (
        <p className="aqsi-card__text" role="alert">
          {problem}
        </p>
      )}
    </section>
  )
}
